import React from 'react';
import {Link} from 'react-router-dom';
import {date} from '@rocket-ui/tools';
import type {Vacancy} from '@rocket-ui/lk-api';
import {getOfferCustomerInitials} from '../../model/offerCustomerInitials';
import {ActionMenu, Button} from 'ui';
import OfferCardPatternIcon from 'shared/assets/svg/offer-card-pattern.svg?react';
import OrderCardUserPlaceholderIcon from 'shared/assets/svg/order-card-user-placeholder.svg?react';
import styles from './OffersTable.module.css';

function getVacancyTitle(vacancy: Vacancy): string {
	return vacancy.name ?? 'Без названия';
}

function getVacancyCustomer(vacancy: Vacancy): string {
	return vacancy.customer?.name ?? 'Не указан';
}

function getVacancyPrice(vacancy: Vacancy): string {
	const amountCents = vacancy.reward?.amountCents;
	if (amountCents == null) return '—';

	return `${new Intl.NumberFormat('ru-RU').format(amountCents / 100).replace(/\u00A0/g, ' ')} ₽`;
}

function getVacancyDeadline(periodTo?: string): string | null {
	if (!periodTo) return null;

	const parsed = new Date(periodTo);
	if (Number.isNaN(parsed.getTime())) return null;

	const currentYear = new Date().getFullYear();
	const formatted = new Intl.DateTimeFormat('ru-RU', {
		day: 'numeric',
		month: 'long',
		...(parsed.getFullYear() !== currentYear ? {year: 'numeric'} : {}),
	}).format(parsed);

	return parsed.getFullYear() !== currentYear ? `до ${formatted} г.` : `до ${formatted}`;
}

function getPublishedAgo(publishedAt?: string | null): string | null {
	if (!publishedAt) return null;
	const parsed = new Date(publishedAt);
	if (Number.isNaN(parsed.getTime())) return null;
	return date.formatDistance(parsed);
}

function AppliedCheckIcon(): React.ReactElement {
	return (
		<svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
			<path
				d="M4 10.5L8 14.5L16 5.5"
				stroke="currentColor"
				strokeWidth="1.6"
				strokeLinecap="round"
				strokeLinejoin="round"
			/>
		</svg>
	);
}

function OfferMobileApplyButton({
	vacancy,
	isApplying,
	onApply,
}: {
	vacancy: Vacancy;
	isApplying: boolean;
	onApply: (vacancy: Vacancy) => void;
}): React.ReactElement | null {
	if (vacancy.applied || vacancy.id == null) return null;

	return (
		<div className={styles.cardFooter}>
			<Button
				type="button"
				fullWidth
				className={styles.cardActionButton}
				disabled={isApplying}
				onClick={(event) => {
					event.preventDefault();
					event.stopPropagation();
					onApply(vacancy);
				}}
			>
				Откликнуться
			</Button>
		</div>
	);
}

export function OffersMobileCard({
	vacancy,
	isApplying,
	onApply,
}: {
	vacancy: Vacancy;
	isApplying: boolean;
	onApply: (vacancy: Vacancy) => void;
}): React.ReactElement {
	const offerId = vacancy.id;
	const title = getVacancyTitle(vacancy);
	const customer = getVacancyCustomer(vacancy);
	const price = getVacancyPrice(vacancy);
	const deadline = getVacancyDeadline(vacancy.periodTo);
	const publishedAgo = getPublishedAgo(vacancy.publishedAt);
	const initials = getOfferCustomerInitials(customer);
	const shareUrl = vacancy.shareUrl?.trim();

	const menuItems = shareUrl
		? [
				{
					key: 'share',
					label: 'Поделиться',
					onSelect: () => {
						void navigator.clipboard?.writeText(shareUrl);
					},
				},
			]
		: [];

	const main = (
		<>
			<div className={styles.cardCustomerRow}>
				<div className={styles.cardAvatar} aria-hidden="true">
					{initials ? (
						<span className={styles.cardAvatarLetters}>{initials}</span>
					) : (
						<OrderCardUserPlaceholderIcon aria-hidden />
					)}
				</div>
				<span className={styles.cardCustomerName}>{customer}</span>
				{menuItems.length > 0 ? (
					<div
						className={styles.cardMenu}
						onClick={(event) => {
							event.preventDefault();
							event.stopPropagation();
						}}
					>
						<ActionMenu items={menuItems} triggerLabel="Действия" />
					</div>
				) : null}
			</div>

			{vacancy.invited && !vacancy.applied ? (
				<span className={styles.cardInviteBadge}>Вас пригласили</span>
			) : null}

			<div className={styles.cardTitle}>{title}</div>
			{deadline ? <p className={styles.cardDeadline}>{deadline}</p> : null}

			<div className={styles.cardPriceRow}>
				<span className={styles.cardPrice}>{price}</span>
				{publishedAgo ? <span className={styles.cardAgo}>{publishedAgo}</span> : null}
			</div>
		</>
	);

	return (
		<article className={styles.mobileCard}>
			{vacancy.applied ? (
				<div className={styles.cardAppliedBar}>
					<span>Вы откликнулись на это предложение</span>
					<AppliedCheckIcon />
				</div>
			) : null}

			<div className={vacancy.applied ? `${styles.cardBody} ${styles.cardBodyWithApplied}` : styles.cardBody}>
				<div className={styles.cardBodyPattern} aria-hidden>
					<OfferCardPatternIcon />
				</div>
				{offerId != null ? (
					<Link to={`/offers/${offerId}`} className={styles.cardBodyLink} aria-label={`Открыть предложение: ${title}`}>
						<div className={styles.cardBodyPad}>{main}</div>
					</Link>
				) : (
					<div className={styles.cardBodyPad}>{main}</div>
				)}
				<OfferMobileApplyButton vacancy={vacancy} isApplying={isApplying} onApply={onApply} />
			</div>
		</article>
	);
}
